import React from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { http, HttpError } from "@/lib/http";
import { useAuth } from "@/app/AuthContext";

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const q = useQuery({
    queryKey: ["auth", "check"],
    queryFn: async () => {
      return http<any>("/api/auth/me");
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  if (q.isLoading) {
    return (
      <div className="h-screen w-screen grid place-items-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (q.isError) {
    const err = q.error as unknown;
    if (err instanceof HttpError && (err.status === 401 || err.status === 403)) {
      return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return (
      <div className="h-screen w-screen grid place-items-center p-6">
        <div className="rounded-xl bg-background shadow p-6">
          <div className="font-semibold">Auth check failed</div>
          <div className="mt-2 text-sm opacity-70">{String((err as any)?.message || err)}</div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex justify-center p-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}

// Organization-MANAGEMENT pages (organizations, access control, special rates):
// a system admin OR an organization manager. A restricted driver — an org member
// who is not a manager — is sent to /forbidden. Backend authorization is
// authoritative; this guard only keeps the driver out of management UI.
export function RequireAdminOrOrg({ children }: { children: React.ReactNode }) {
  const { isAdmin, isManager, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex justify-center p-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAdmin && !isManager) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}
